import { useEffect, useMemo, useRef, useState } from "react";
import { subscribe } from "../../lib/hyperliquid";
import { fetchInfo } from "../../lib/hyperliquid";
import { formatPrice } from "../../lib/format";

export default function MarketSelector({ coin, quote = "USD", onSelect }) {
    const [mids, setMids] = useState({});
    const [universe, setUniverse] = useState([]);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const isMulti = quote && quote !== "USD";

    useEffect(() => {
        fetchInfo({ type: "meta" }).then((m) => setUniverse(m.universe || [])).catch(() => {});
        const unsub = subscribe({ type: "allMids" }, (d) => setMids(d.mids || {}));
        return unsub;
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const list = useMemo(() => {
        const names = universe.map((u) => u.name).filter(Boolean);
        const q = search.trim().toUpperCase();
        const filtered = q ? names.filter((n) => n.includes(q)) : names;
        return filtered.slice(0, 40);
    }, [universe, search]);

    // For multi pairs we show the live ratio in the header pill.
    let headerPrice = "—";
    if (isMulti) {
        const base = parseFloat(mids[coin]);
        const qm = parseFloat(mids[quote]);
        if (base > 0 && qm > 0) {
            const r = base / qm;
            headerPrice = r >= 1000 ? formatPrice(r) : r >= 1 ? r.toFixed(4) : r.toFixed(6);
        }
    } else {
        headerPrice = mids[coin] ? formatPrice(+mids[coin]) : "—";
    }

    return (
        <div ref={containerRef} className="relative">
            <button
                data-testid="market-selector-btn"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-3 bg-[#0A0A0C] border border-[#1F1F24] hover:border-zinc-700 rounded-sm px-4 py-2 transition-colors"
            >
                <div className="text-left">
                    <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">{isMulti ? "Cross market" : "Market"}</div>
                    <div className="font-mono text-lg font-medium tracking-tight flex items-center gap-1.5">
                        {coin}-{quote}
                        {isMulti && <span className="text-[8.5px] uppercase tracking-[0.18em] text-violet-300 border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 rounded-sm">synthetic</span>}
                    </div>
                </div>
                <div className="text-cyan-400 font-mono text-lg font-medium">{headerPrice}</div>
                <svg width="14" height="14" viewBox="0 0 16 16" className="text-zinc-500 ml-1"><path fill="currentColor" d="M4 6l4 4 4-4z"/></svg>
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1.5 w-80 bg-[#0A0A0C] border border-[#1F1F24] rounded-sm z-30 shadow-2xl">
                    <div className="p-2 border-b border-[#1F1F24]">
                        <input
                            data-testid="market-search-input"
                            autoFocus
                            placeholder="search 230+ Hyperliquid markets…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full bg-transparent outline-none font-mono text-xs px-2 py-1 placeholder:text-zinc-600"
                        />
                    </div>
                    <div className="max-h-[360px] overflow-y-auto">
                        {list.map((name) => (
                            <button
                                key={name}
                                data-testid={`market-option-${name}`}
                                onClick={() => { onSelect(name); setOpen(false); setSearch(""); }}
                                className={`w-full grid grid-cols-[1fr_auto] items-center px-3 py-1.5 text-left text-xs font-mono hover:bg-[#141417] ${coin === name ? "bg-[#141417]" : ""}`}
                            >
                                <span className="text-zinc-200">{name}-USD</span>
                                <span className={`${coin === name ? "text-cyan-400" : "text-zinc-400"}`}>{mids[name] ? formatPrice(+mids[name]) : "—"}</span>
                            </button>
                        ))}
                        {list.length === 0 && (
                            <div className="px-3 py-6 text-center text-[11px] font-mono text-zinc-600">no markets matched</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
